import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { academyAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  BookOpenIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  LockClosedIcon,
  PlayCircleIcon
} from '@heroicons/react/24/outline';

const getEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
      let id = u.searchParams.get('v');
      if (!id && u.hostname.includes('youtu.be')) id = u.pathname.slice(1);
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes('vimeo.com')) {
      const id = u.pathname.split('/').filter(Boolean).pop();
      if (id) return `https://player.vimeo.com/video/${id}`;
    }
  } catch {
    return null;
  }
  return null;
};

const AcademyCourse = () => {
  const { slug } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [activeLessonId, setActiveLessonId] = useState(null);

  useEffect(() => {
    loadCourse();
  }, [slug, user]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const res = await academyAPI.getCourse(slug);
      setCourse(res.data.course);
      setLessons(res.data.lessons || []);
      const first = res.data.lessons?.[0];
      if (first && !first.locked) setActiveLessonId(first.id);
      else setActiveLessonId(null);
    } catch (error) {
      console.error('Load course error:', error);
    } finally {
      setLoading(false);
    }
  };

  const activeLesson = lessons.find((l) => l.id === activeLessonId);
  const embedUrl = activeLesson?.videoUrl ? getEmbedUrl(activeLesson.videoUrl) : null;
  const accountType = user?.accountType || 'farmer';
  const canSubscribe = !user || accountType === 'farmer' || accountType === 'supplier';
  const billingPath = accountType === 'supplier' ? '/supplier/settings' : '/billing';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-primary-50 to-white">
        <LoadingSpinner />
      </div>
    );
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex flex-col items-center justify-center px-6">
        <BookOpenIcon className="h-12 w-12 text-gray-400 mb-4" />
        <h1 className="text-xl font-semibold text-gray-900">Course not found</h1>
        <Link to="/academy" className="mt-4 text-primary-700 hover:underline">Back to Academy</Link>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-primary-50 via-white to-white">
      <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/academy" className="shrink-0 p-2 rounded-lg hover:bg-gray-100 text-gray-600">
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div className="min-w-0">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Omabra Academy</p>
              <h1 className="text-lg font-semibold text-gray-900 truncate">{course.title}</h1>
            </div>
          </div>
          {canSubscribe && (
            <Link to={user ? billingPath : '/register'} className="btn-primary text-sm shrink-0">
              {user ? 'Manage subscription' : 'Subscribe'}
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {course.isLocked ? (
          <LockedCourseView course={course} lessons={lessons} user={user} />
        ) : (
          <div className="grid lg:grid-cols-[280px_1fr] gap-8">
            <aside className="lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                <div className="p-4 border-b bg-gray-50">
                  <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                    <BookOpenIcon className="h-5 w-5 text-primary-600" />
                    Lessons
                  </h2>
                  <p className="text-xs text-gray-500 mt-1">{lessons.length} modules</p>
                </div>
                <nav className="max-h-[60vh] overflow-y-auto">
                  {lessons.map((lesson, idx) => (
                    <button
                      key={lesson.id}
                      onClick={() => !lesson.locked && setActiveLessonId(lesson.id)}
                      disabled={lesson.locked}
                      className={`w-full text-left px-4 py-3 border-b border-gray-50 transition-colors ${
                        activeLessonId === lesson.id
                          ? 'bg-primary-50 border-l-4 border-l-primary-600'
                          : lesson.locked
                            ? 'opacity-60 cursor-not-allowed'
                            : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-xs font-medium text-gray-400 mt-0.5">{idx + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{lesson.title}</p>
                          {lesson.durationMinutes > 0 && (
                            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                              <ClockIcon className="h-3 w-3" />
                              {lesson.durationMinutes} min
                            </p>
                          )}
                        </div>
                        {lesson.locked && <LockClosedIcon className="h-4 w-4 text-gray-400 shrink-0" />}
                      </div>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            <section className="min-w-0">
              {activeLesson ? (
                <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                  <div className="p-6 sm:p-8 border-b border-gray-100">
                    <h2 className="text-2xl font-bold text-gray-900">{activeLesson.title}</h2>
                  </div>

                  {embedUrl && (
                    <div className="aspect-video bg-black">
                      <iframe
                        src={embedUrl}
                        title={activeLesson.title}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  )}

                  {!embedUrl && activeLesson.videoUrl && (
                    <div className="p-6 bg-gray-50 border-b">
                      <a
                        href={activeLesson.videoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-primary-700 font-medium hover:underline"
                      >
                        <PlayCircleIcon className="h-5 w-5" />
                        Watch video
                      </a>
                    </div>
                  )}

                  {activeLesson.content && (
                    <div className="p-6 sm:p-8 prose prose-gray max-w-none">
                      <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                        {activeLesson.content}
                      </div>
                    </div>
                  )}

                  {activeLesson.attachmentUrl && (
                    <div className="p-6 sm:p-8 border-t bg-gray-50">
                      <a
                        href={activeLesson.attachmentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm font-medium text-gray-800 hover:bg-gray-50 shadow-sm"
                      >
                        <DocumentArrowDownIcon className="h-5 w-5 text-primary-600" />
                        Download resource
                      </a>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-12 text-center">
                  <AcademicCapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Select a lesson to begin studying.</p>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

const LockedCourseView = ({ course, lessons, user }) => {
  const accountType = user?.accountType || 'farmer';
  const isClient = !!user && accountType === 'client';
  const billingUrl = accountType === 'supplier' ? '/supplier/settings' : '/billing';

  return (
  <div className="max-w-2xl mx-auto">
    <div className="rounded-2xl border border-gray-100 bg-white shadow-lg overflow-hidden">
      {course.thumbnailUrl && (
        <div className="h-48 relative">
          <img src={course.thumbnailUrl} alt={course.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
            <LockClosedIcon className="h-12 w-12 text-white" />
          </div>
        </div>
      )}
      <div className="p-8 text-center">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100 mb-4">
          <LockClosedIcon className="h-7 w-7 text-amber-700" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Subscriber-only course</h2>
        <p className="mt-3 text-gray-600">
          {isClient
            ? 'The first two academy courses are free for everyone. The full library is part of a paid subscription, which is available on farmer and supplier accounts.'
            : 'The first two academy courses are free for everyone. This one is included with a paid Omabra subscription, available on farmer and supplier accounts.'}
        </p>
        {course.description && (
          <p className="mt-4 text-sm text-gray-500">{course.description}</p>
        )}
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
          {!isClient && (
            <Link to={user ? billingUrl : '/register'} className="btn-primary w-full sm:w-auto">
              {user ? 'Upgrade subscription' : 'Subscribe now'}
            </Link>
          )}
          <Link to="/academy" className={`w-full sm:w-auto ${isClient ? 'btn-primary' : 'btn-outline'}`}>
            Browse free courses
          </Link>
        </div>
        {lessons.length > 0 && (
          <div className="mt-8 text-left border-t pt-6">
            <p className="text-sm font-medium text-gray-700 mb-3">{lessons.length} lessons included:</p>
            <ul className="space-y-2">
              {lessons.map((l) => (
                <li key={l.id} className="flex items-center gap-2 text-sm text-gray-500">
                  <LockClosedIcon className="h-4 w-4 shrink-0" />
                  {l.title}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export default AcademyCourse;
